import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    SearchX,
    Loader2,
    Sparkles,
    X,
    TrendingUp,
    CheckCircle2,
    CircleOff,
} from "lucide-react";
import { toast } from "sonner";
import { api, fmtEUR, tradeLabel } from "@/lib/api";

const SEV = {
    high: {
        cls: "bg-danger text-white border-danger",
        label: "Critique",
    },
    medium: {
        cls: "bg-warn text-white border-warn",
        label: "Important",
    },
    low: {
        cls: "bg-ink-soft text-white border-ink-soft",
        label: "Mineur",
    },
};

export default function OmissionsSection({ projectId, refreshKey, devisCount }) {
    const [alerts, setAlerts] = useState([]);
    const [running, setRunning] = useState(false);
    const [loading, setLoading] = useState(true);
    const [hasRunOnce, setHasRunOnce] = useState(false);

    const load = async () => {
        try {
            const data = await api.listOmissions(projectId);
            setAlerts(data);
            if (data.length > 0) setHasRunOnce(true);
        } catch (e) {
            console.error("OmissionsSection load failed:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [projectId, refreshKey]);

    const run = async () => {
        if (devisCount < 2) {
            toast.error("Il faut au moins 2 devis du même corps de métier.");
            return;
        }
        setRunning(true);
        try {
            const r = await api.detectOmissions(projectId, "claude");
            setAlerts(r.alerts);
            setHasRunOnce(true);
            if (r.count === 0) {
                toast.success(
                    "Aucun poste oublié détecté — les devis sont cohérents."
                );
            } else {
                toast.success(
                    `${r.count} poste${r.count > 1 ? "s" : ""} potentiellement oublié${
                        r.count > 1 ? "s" : ""
                    }`
                );
            }
            if (r.errors?.length) {
                toast.error(
                    `Analyse incomplète : ${r.errors.length} lot(s) en erreur`
                );
            }
        } catch {
            toast.error("Erreur de détection");
        } finally {
            setRunning(false);
        }
    };

    const dismiss = async (alertId) => {
        await api.dismissOmission(projectId, alertId);
        setAlerts((prev) => prev.filter((a) => a.id !== alertId));
    };

    if (loading) return null;

    // Empty state: only show the section if user has at least 2 devis
    const showEmpty = alerts.length === 0;
    if (showEmpty && devisCount < 2) return null;

    const totalRisk = alerts.reduce(
        (acc, a) => acc + (a.estimated_amount || 0) * (a.missing_in?.length || 0),
        0
    );

    return (
        <section
            className={`panel ${alerts.length > 0 ? "border-warn" : ""}`}
            data-testid="omissions-section"
        >
            <div
                className={`panel-header ${
                    alerts.length > 0 ? "bg-warn/5 border-warn" : ""
                }`}
            >
                <div className="flex items-center gap-3">
                    <SearchX
                        className={`w-5 h-5 ${
                            alerts.length > 0 ? "text-warn" : "text-ink-soft"
                        }`}
                        strokeWidth={1.8}
                    />
                    <div>
                        <div className="stat-label">
                            Postes potentiellement oubliés
                        </div>
                        <h3 className="font-heading text-lg font-bold mt-0.5">
                            {alerts.length === 0
                                ? hasRunOnce
                                    ? "Aucun oubli détecté"
                                    : "Analyse à lancer"
                                : `${alerts.length} poste${alerts.length > 1 ? "s" : ""} manquant${alerts.length > 1 ? "s" : ""}`}
                            {totalRisk > 0 && (
                                <span className="text-warn ml-2 text-sm num font-bold">
                                    · risque {fmtEUR(totalRisk)}
                                </span>
                            )}
                        </h3>
                    </div>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={run}
                    disabled={running || devisCount < 2}
                    className="rounded-none border-ink"
                    data-testid="detect-omissions-btn"
                >
                    {running ? (
                        <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                    ) : (
                        <Sparkles
                            className="w-3.5 h-3.5 mr-2"
                            strokeWidth={1.6}
                        />
                    )}
                    {alerts.length > 0 ? "Ré-analyser" : "Détecter les oublis"}
                </Button>
            </div>

            {alerts.length === 0 ? (
                <div className="p-8 text-center">
                    {hasRunOnce ? (
                        <>
                            <CheckCircle2
                                className="w-10 h-10 mx-auto text-ok mb-3"
                                strokeWidth={1.5}
                            />
                            <div className="font-heading text-lg font-bold mb-1">
                                Devis cohérents
                            </div>
                            <p className="text-sm text-ink-soft max-w-md mx-auto">
                                L'IA n'a trouvé aucun poste présent dans
                                certains devis et oublié dans d'autres.
                            </p>
                        </>
                    ) : (
                        <>
                            <CircleOff
                                className="w-10 h-10 mx-auto text-ink-soft mb-3"
                                strokeWidth={1.5}
                            />
                            <p className="text-sm text-ink-soft max-w-md mx-auto">
                                Comparez les devis d'un même corps de métier
                                pour identifier les prestations chiffrées par
                                certains artisans mais oubliées par les autres
                                — sources d'avenants coûteux.
                            </p>
                        </>
                    )}
                </div>
            ) : (
                <div className="divide-y divide-border">
                    {alerts.map((a) => {
                        const sev = SEV[a.severity] || SEV.medium;
                        return (
                            <div
                                key={a.id}
                                data-testid={`omission-${a.id}`}
                                className="px-6 py-4 flex items-start gap-4 hover:bg-[#FAFAFA]"
                            >
                                <Badge
                                    className={`rounded-none uppercase text-[10px] tracking-wider shrink-0 ${sev.cls}`}
                                >
                                    {sev.label}
                                </Badge>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline gap-2 flex-wrap">
                                        <div className="font-heading font-bold text-base">
                                            {a.item_description}
                                        </div>
                                        <span className="pill border-ink/30 bg-white text-ink-soft">
                                            {tradeLabel(a.trade)}
                                        </span>
                                        <span className="text-warn num font-bold inline-flex items-center gap-1 ml-auto">
                                            <TrendingUp
                                                className="w-3.5 h-3.5"
                                                strokeWidth={2}
                                            />
                                            ≈ {fmtEUR(a.estimated_amount)}
                                        </span>
                                    </div>
                                    <p className="text-xs text-ink-soft mt-1.5 leading-relaxed">
                                        {a.reason}
                                    </p>
                                    <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
                                        <div className="border-l-2 border-ok pl-2.5">
                                            <div className="stat-label text-ok">
                                                Présent chez
                                            </div>
                                            <ul className="mt-1 space-y-0.5">
                                                {a.present_in.map((p) => (
                                                    <li
                                                        key={p.devis_id}
                                                        className="text-ink"
                                                    >
                                                        ✓ {p.contractor}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="border-l-2 border-danger pl-2.5">
                                            <div className="stat-label text-danger">
                                                Absent chez
                                            </div>
                                            <ul className="mt-1 space-y-0.5">
                                                {a.missing_in.map((p) => (
                                                    <li
                                                        key={p.devis_id}
                                                        className="text-ink"
                                                    >
                                                        ✕ {p.contractor}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => dismiss(a.id)}
                                    className="shrink-0 text-ink-soft hover:text-danger p-1"
                                    title="Ignorer cette alerte"
                                    data-testid={`dismiss-omission-${a.id}`}
                                >
                                    <X
                                        className="w-4 h-4"
                                        strokeWidth={1.5}
                                    />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
}
